import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseDialog from './BaseDialog';
import PlusMinusButton from './PlusMinusButton';
import { videoService } from '../../services/VideoService';
import { getVideoAspectRatios, getVideoResolutions, getVideoFps } from '../../utils/api';

// 定义ImageToVideoDialog的props接口
interface ImageToVideoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sceneId: string;
  sceneNumber: number;
  initialPrompt: string;
  currentImages: string[];
  onUpdateVideoTaskId: (sceneId: string, taskId: string) => void;
  onUpdateVideoStatus: (sceneId: string, status: string) => void;
  onShowToast: (message: string) => void;
  sceneList?: Array<{ id: string; images: string[] }>; // 添加sceneList用于上下文图片
}

/**
 * 图片转视频对话框组件
 * - 继承BaseDialog的基础样式和布局
 * - 专门用于图片到视频的生成功能
 * - 包含视频描述编辑、参数配置和上下文图片选择
 */
const ImageToVideoDialog: React.FC<ImageToVideoDialogProps> = ({
  isOpen,
  onClose,
  sceneId,
  sceneNumber,
  initialPrompt,
  currentImages,
  onUpdateVideoTaskId,
  onUpdateVideoStatus,
  onShowToast,
  sceneList = []
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [imageRoles, setImageRoles] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  // 视频参数
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('720p');
  const [fps, setFps] = useState(30);
  const [cameraMovement, setCameraMovement] = useState('static');
  const [style, setStyle] = useState('cinematic');
  const [watermark, setWatermark] = useState(false);

  // 支持的参数选项
  const [aspectRatios, setAspectRatios] = useState<string[]>(['16:9', '9:16', '1:1', '4:3']);
  const [resolutions, setResolutions] = useState<string[]>(['720p', '1080p']);
  const [fpsOptions, setFpsOptions] = useState<number[]>([24, 30, 60]);

  // 当对话框打开时，同步状态
  useEffect(() => {
    if (isOpen) {
      setPrompt(initialPrompt);
      // 默认选择当前场景的第一张图片
      if (currentImages.length > 0) {
        setSelectedImages([currentImages[0]]);
        setImageRoles(['first_frame']);
      } else {
        setSelectedImages([]);
        setImageRoles([]);
      }
      setIsGenerating(false);
      // 加载支持的视频参数
      loadVideoParams();
    }
  }, [isOpen, initialPrompt, currentImages]);

  // 加载支持的视频参数
  const loadVideoParams = async () => {
    try {
      const [aspectRatiosRes, resolutionsRes, fpsRes] = await Promise.all([
        getVideoAspectRatios(),
        getVideoResolutions(),
        getVideoFps()
      ]);

      if (aspectRatiosRes.code === 200 && aspectRatiosRes.data?.aspect_ratios) {
        setAspectRatios(aspectRatiosRes.data.aspect_ratios);
      }

      if (resolutionsRes.code === 200 && resolutionsRes.data?.resolutions) {
        setResolutions(resolutionsRes.data.resolutions);
      }

      if (fpsRes.code === 200 && fpsRes.data?.fps) {
        setFpsOptions(fpsRes.data.fps);
      }
    } catch (error) {
      console.error('加载视频参数失败:', error);
    }
  };

  // 获取所有可用的上下文图片
  const getAllContextImages = () => {
    const images: Array<{ url: string; sceneId: string }> = [];
    sceneList.forEach(scene => {
      scene.images.forEach(imageUrl => {
        images.push({ url: imageUrl, sceneId: scene.id });
      });
    });
    return images;
  };

  // 切换图片选择（单选）
  const toggleImageSelection = (imageUrl: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        // 移除图片
        setImageRoles([]);
        return [];
      } else {
        // 单选：只保留当前选择的图片
        setImageRoles(['first_frame']);
        return [imageUrl];
      }
    });
  };

  // 更新图片角色
  const updateImageRole = (index: number, newRole: string) => {
    setImageRoles(prev => {
      const newRoles = [...prev];
      newRoles[index] = newRole;
      return newRoles;
    });
  };

  // 处理生成视频
  const handleGenerateVideo = async () => {
    if (selectedImages.length === 0) {
      onShowToast(t('toast.imageToVideo.selectAtLeastOneImage'));
      return;
    }

    if (prompt.trim() === '') {
      onShowToast(t('toast.imageToVideo.enterVideoDescription'));
      return;
    }

    setIsGenerating(true);

    try {
      // 创建请求参数
      const requestParams = {
        prompt: prompt,
        image_urls: selectedImages,
        image_roles: imageRoles,
        duration: duration,
        aspect_ratio: aspectRatio,
        resolution: resolution,
        fps: fps,
        camera_movement: cameraMovement,
        style: style,
        watermark: watermark
      };

      // 显示toast提示
      const message = `${t('toast.common.processing')}\n${t('dialog.imageToVideo.videoDescription')}: ${requestParams.prompt.slice(0, 20)}...\n${t('dialog.imageToVideo.durationLabel')}: ${requestParams.duration}${t('dialog.imageToVideo.seconds')}\n${t('dialog.imageToVideo.resolutionLabel')}: ${requestParams.resolution}`;
      onShowToast(message);

      // 调用视频生成服务
      const taskId = await videoService.generateVideo(sceneId, requestParams);

      // 更新视频任务ID和状态
      onUpdateVideoTaskId(sceneId, taskId);
      onUpdateVideoStatus(sceneId, 'processing');

      // 显示成功提示
      onShowToast(t('toast.imageToVideo.taskSubmitted', { taskId }));

      // 关闭对话框
      onClose();
    } catch (error) {
      console.error('生成视频失败:', error);
      onShowToast(t('toast.imageToVideo.generationFailed'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialog.imageToVideo.title', { sceneNumber })}
      width={800}
      height={600}
    >
      <div className="space-y-4">
        {/* 视频描述编辑区域 */}
        <div className="flex flex-col gap-2 relative">
          <label className="text-sm text-gray-400">{t('dialog.imageToVideo.videoDescription')}</label>
          <textarea
            className="p-4 bg-gray-900/80 rounded-lg border border-gray-700/50 whitespace-pre-wrap text-sm text-white resize-y min-h-[100px] pr-16"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('dialog.imageToVideo.descriptionPlaceholder')}
          />
        </div>

        {/* 上下文图片选择区域 */}
        <div className="flex flex-col gap-2">
          <div className="text-sm text-gray-400 mb-2">{t('dialog.imageToVideo.contextImagesLabel')}</div>
          <div className="bg-gray-900/40 rounded-lg p-3 border border-gray-700/30">
            <div className="grid grid-cols-6 gap-2 max-h-40 overflow-y-auto">
              {getAllContextImages().map((image, index) => (
                <div
                  key={index}
                  className={`relative aspect-square rounded cursor-pointer border-2 transition-all ${selectedImages.includes(image.url)
                    ? 'border-blue-500 ring-2 ring-blue-500/30'
                    : 'border-gray-700/50 hover:border-gray-600'
                    }`}
                  onClick={() => toggleImageSelection(image.url)}
                >
                  <img
                    src={image.url}
                    alt={`上下文图片 ${index + 1}`}
                    className="w-full h-full object-cover rounded"
                  />
                  {selectedImages.includes(image.url) && (
                    <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 已选择图片和角色配置 */}
        {selectedImages.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-sm text-gray-400 mb-2">{t('dialog.imageToVideo.selectedImages')}</div>
            <div className="bg-gray-900/40 rounded-lg p-3 border border-gray-700/30">
              <div className="space-y-2">
                {selectedImages.map((imageUrl, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded overflow-hidden">
                      <img
                        src={imageUrl}
                        alt={`已选择图片 ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <select
                        value={imageRoles[index] || 'first_frame'}
                        onChange={(e) => updateImageRole(index, e.target.value)}
                        className="bg-gray-800/80 text-sm text-white rounded border border-gray-700/50 px-2 py-1 focus:outline-none focus:ring-blue-500/50"
                      >
                        <option value="first_frame">{t('dialog.imageToVideo.firstFrame')}</option>
                        <option value="last_frame">{t('dialog.imageToVideo.lastFrame')}</option>
                        <option value="reference_image">{t('dialog.imageToVideo.referenceImage')}</option>
                      </select>
                    </div>
                    <button
                      className="text-red-400 hover:text-red-300 text-sm"
                      onClick={() => toggleImageSelection(imageUrl)}
                    >
                      {t('dialog.imageToVideo.delete')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 视频参数配置区域 */}
        <div className="space-y-4">
          <div className="text-sm text-gray-400">{t('dialog.imageToVideo.videoParams')}</div>

          <div className="grid grid-cols-2 gap-4">
            {/* 视频时长 */}
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-500">{t('dialog.imageToVideo.durationLabel')}</label>
              <PlusMinusButton
                value={duration}
                onValueChange={setDuration}
                min={3}
                max={10}
              />
            </div>

            {/* 视频比例 */}
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-500">{t('dialog.imageToVideo.aspectRatioLabel')}</label>
              <select
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value)}
                className="bg-gray-800/80 text-sm text-white rounded border border-gray-700/50 px-2 py-1 focus:outline-none focus:ring-blue-500/50"
              >
                {aspectRatios.map(ratio => (
                  <option key={ratio} value={ratio}>{ratio}</option>
                ))}
              </select>
            </div>

            {/* 分辨率 */}
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-500">{t('dialog.imageToVideo.resolutionLabel')}</label>
              <select
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="bg-gray-800/80 text-sm text-white rounded border border-gray-700/50 px-2 py-1 focus:outline-none focus:ring-blue-500/50"
              >
                {resolutions.map(res => (
                  <option key={res} value={res}>{res}</option>
                ))}
              </select>
            </div>

            {/* 帧率 */}
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-500">{t('dialog.imageToVideo.fpsLabel')}</label>
              <select
                value={fps}
                onChange={(e) => setFps(Number(e.target.value))}
                className="bg-gray-800/80 text-sm text-white rounded border border-gray-700/50 px-2 py-1 focus:outline-none focus:ring-blue-500/50"
              >
                {fpsOptions.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>

            {/* 摄像机运动 */}
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-500">{t('dialog.imageToVideo.cameraMovementLabel')}</label>
              <select
                value={cameraMovement}
                onChange={(e) => setCameraMovement(e.target.value)}
                className="bg-gray-800/80 text-sm text-white rounded border border-gray-700/50 px-2 py-1 focus:outline-none focus:ring-blue-500/50"
              >
                <option value="static">{t('dialog.imageToVideo.cameraMovement.static')}</option>
                <option value="pan">{t('dialog.imageToVideo.cameraMovement.pan')}</option>
                <option value="zoom">{t('dialog.imageToVideo.cameraMovement.zoom')}</option>
                <option value="rotate">{t('dialog.imageToVideo.cameraMovement.rotate')}</option>
              </select>
            </div>

            {/* 视频风格 */}
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-500">{t('dialog.imageToVideo.videoStyleLabel')}</label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="bg-gray-800/80 text-sm text-white rounded border border-gray-700/50 px-2 py-1 focus:outline-none focus:ring-blue-500/50"
              >
                <option value="cinematic">{t('dialog.imageToVideo.videoStyle.cinematic')}</option>
                <option value="animated">{t('dialog.imageToVideo.videoStyle.animated')}</option>
                <option value="realistic">{t('dialog.imageToVideo.videoStyle.realistic')}</option>
              </select>
            </div>

            {/* 水印选项 */}
            <div className="flex items-center gap-2 col-span-2">
              <input
                type="checkbox"
                id="watermark"
                checked={watermark}
                onChange={(e) => setWatermark(e.target.checked)}
                className="rounded bg-gray-900/80 border-gray-700/50 text-blue-500 focus:ring-blue-500/50"
              />
              <label htmlFor="watermark" className="text-sm text-gray-400 cursor-pointer">
                {t('dialog.imageToVideo.addWatermark')}
              </label>
            </div>
          </div>
        </div>

        {/* 生成按钮 */}
        <div className="flex justify-end mt-6">
          <button
            className={`px-6 py-2 rounded-lg font-medium transition-all ${isGenerating
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            onClick={handleGenerateVideo}
            disabled={isGenerating}
          >
            {isGenerating ? t('dialog.imageToVideo.generating') : t('dialog.imageToVideo.generateVideo')}
          </button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default ImageToVideoDialog;